import { Plus } from 'lucide-react'
import { useEffect } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ds2'
import { useAppSelector } from '@/hooks'
import { selectApplication, selectFeatureFlags, selectUser } from '@/store/authSlice'
import { GeneralAgentChat } from '../components/GeneralAgentChat'
import { GeneralAgentHistory } from '../components/GeneralAgentHistory'
import { FactoringStatusChip } from '../components/FactoringStatusChip'
import { InvoiceReviewDrawer } from '../components/invoicing/review/InvoiceReviewDrawer'
import { useInvoiceReview } from '../components/invoicing/review/reviewStore'
import { GENERAL_AGENT_PAGE_PATH, isAgentTabId } from '../constants'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'

/**
 * The Agent page (v17): one panel with the top bar "Invoicing chat", the chat history select and "＋ New chat", and the chat
 * centred in it. Opening `/agent` starts a new empty chat; `/agent/c/:tabId` opens that chat, and the address moves there as soon as a
 * chat is on screen, so a reload keeps it. The store holds the conversation.
 */
export const GeneralAgentPage = () => {
    const { tabId: routeTab } = useParams<{ tabId: string }>()
    const navigate = useNavigate()
    const enabled = useAppSelector(selectFeatureFlags).generalAgent ?? false
    const user = useAppSelector(selectUser)
    const application = useAppSelector(selectApplication)

    const store = useGeneralAgentChatStore
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const owner = useGeneralAgentChatStore((s) => s.owner)
    const bindOwner = useGeneralAgentChatStore((s) => s.bindOwner)
    const openChat = useGeneralAgentChatStore((s) => s.openChat)
    const reset = useGeneralAgentChatStore((s) => s.reset)
    const restore = useGeneralAgentChatStore((s) => s.restore)

    // The chat belongs to who is signed in, in which company: another user or company starts clean (logout is not a reload).
    const userId = user?.id
    const projectName = application?.projectName
    useEffect(() => {
        if (userId && projectName) bindOwner(`${userId}:${projectName}`)
    }, [userId, projectName, bindOwner])

    // The page opens a new empty chat; a chat URL opens that chat. A route id that is not a tab id is redirected below, never sent.
    // Only once the owner is bound: binding resets the chat to the owner's saved one, so a chat opened before it would be wiped.
    useEffect(() => {
        if (!owner) return
        if (!isAgentTabId(routeTab)) reset()
        else if (store.getState().tabId !== routeTab) void openChat(routeTab)
        // A reload: the owner's saved chat is this one, but only its id is back, so its messages are read once.
        else void restore()
    }, [owner, routeTab, openChat, reset, restore, store])

    // The address follows the chat on screen (a new chat's first message, a chat opened from History), so a reload reopens it.
    // The store's chat, not this render's: the route effect above may have just opened another one in the same commit.
    useEffect(() => {
        const current = store.getState().tabId
        if (owner && current && current !== routeTab) navigate(`${GENERAL_AGENT_PAGE_PATH}/c/${current}`, { replace: true })
    }, [owner, tabId, routeTab, navigate, store])

    // A review belongs to the chat on screen: leaving the page closes it.
    useEffect(() => () => useInvoiceReview.getState().close(), [])

    if (!enabled) return <Navigate to="/" replace />
    if (routeTab !== undefined && !isAgentTabId(routeTab)) return <Navigate to={GENERAL_AGENT_PAGE_PATH} replace />

    // "New chat" from a chat URL leaves it for the plain page, so the address names the chat on screen.
    const newChat = () => {
        reset()
        if (routeTab) navigate(GENERAL_AGENT_PAGE_PATH)
    }

    return (
        <div className="flex h-full min-h-0 flex-col bg-background p-3 text-foreground md:p-4">
            <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-surface">
                <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-3">
                    <h2 className="text-sm font-medium text-foreground">Invoicing chat</h2>
                    <FactoringStatusChip />
                    <div className="ml-auto flex items-center gap-2">
                        <GeneralAgentHistory currentTabId={tabId} onOpen={(id) => void openChat(id)} />
                        <Button variant="ghost" size="sm" prefix={<Plus className="h-3.5 w-3.5" aria-hidden />} onClick={newChat}>
                            New chat
                        </Button>
                    </div>
                </header>
                <GeneralAgentChat />
            </section>
            <InvoiceReviewDrawer />
        </div>
    )
}
